import { ReactNode } from 'react';
import { Card, GaugeChart, withStyles, EnrichedRiskReport, EnrichedTransactionReport } from 'vopay';
import { useTranslation } from 'react-i18next';
import MetricTooltip from '../MetricTooltip';
import useMetricTooltips from '../useMetricTooltips';
import { formatAmount } from '../formatAmount';
import { riskLevel } from '../insightsModel';
import styles from './RiskMeterCard.module.scss';

const cn = withStyles(styles);

type RiskMeterCardProps = {
  riskReport: EnrichedRiskReport;
  transactionReport: EnrichedTransactionReport | null;
  // NSF fee total within the visible date range
  nsfFeeTotal: number;
  currency: string;
  onClick: () => void;
};

export default function RiskMeterCard({
  riskReport,
  transactionReport,
  nsfFeeTotal,
  currency,
  onClick,
}: RiskMeterCardProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Customers.PaymentMethodsTab.DataEnrichment.Risk' });
  const tooltips = useMetricTooltips();

  const riskLabels = {
    low: t('lowRisk', { defaultValue: 'Low Risk' }),
    moderate: t('moderateRisk', { defaultValue: 'Moderate Risk' }),
    high: t('highRisk', { defaultValue: 'High Risk' }),
    veryHigh: t('veryHighRisk', { defaultValue: 'Very High Risk' }),
  };

  const dataRows: { label: string; tooltip?: string; value: ReactNode }[] = [
    {
      label: t('nsfFee', { defaultValue: 'NSF Fee' }),
      tooltip: tooltips.nsfFee,
      value: formatAmount(nsfFeeTotal, currency),
    },
    {
      label: t('nsfRate', { defaultValue: 'NSF Rate' }),
      tooltip: tooltips.nsfRate,
      value: `${(riskReport.NSFRate * 100).toFixed(2)}%`,
    },
    {
      label: t('avgNsfAmount', { defaultValue: 'Avg. NSF Amount' }),
      tooltip: tooltips.avgNsfAmount,
      value: transactionReport ? formatAmount(transactionReport.AverageNSFAmount, currency) : '—',
    },
    {
      label: t('percentile90Nsf', { defaultValue: '90th Percentile NSF' }),
      value: transactionReport ? formatAmount(transactionReport['90thPercentileNSFAmount'], currency) : '—',
    },
    {
      label: t('overdraft', { defaultValue: 'Overdraft' }),
      tooltip: tooltips.overdraft,
      value: riskReport.Overdrafts,
    },
  ];

  return (
    <div
      className={cn('clickableWrap')}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') onClick();
      }}
      data-testid="risk-meter-card"
    >
      <Card className={cn('container')}>
        <div className={cn('meterRow')}>
          <GaugeChart value={riskReport.RiskIndex} max={100} />

          <div className={cn('meterLabels')}>
            <span className={cn('riskLabel')}>{riskLabels[riskLevel(riskReport.RiskIndex)]}</span>
            <span className={cn('riskSub')}>{t('riskScore', { defaultValue: 'Risk Score' })}</span>
          </div>
        </div>

        {dataRows.map(({ label, tooltip, value }) => (
          <div key={label} className={cn('dataRow')}>
            <span className={cn('dataLabel')}>
              {label}
              {tooltip && <MetricTooltip information={tooltip} />}
            </span>

            <span className={cn('dataValue')}>{value}</span>
          </div>
        ))}
      </Card>
    </div>
  );
}
